// oxlint-disable import/first
import { captureConsoleIntegration } from "@sentry/core";
import * as Sentry from "@sentry/react-native";
import { isRunningInExpoGo } from "expo";
import * as Updates from "expo-updates";

export type RoutingIntegration = ReturnType<
  typeof Sentry.reactNavigationIntegration
>;

// Construct a new instrumentation instance. This is needed to communicate between the integration and React
export const routingIntegration: RoutingIntegration =
  Sentry.reactNavigationIntegration({
    enableTimeToInitialDisplay: !isRunningInExpoGo(),
  });

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  enableNativeFramesTracking: !isRunningInExpoGo(), // Tracks slow and frozen frames in the application
  environment: __DEV__ ? `development` : `production`,
  integrations: [
    captureConsoleIntegration({
      // Only send "problems" to Sentry, the others are often used in
      // development and are too noisey to send to Sentry.
      levels: [`warn`, `error`],
    }),
    routingIntegration,
  ],
  tracesSampleRate: __DEV__ ? 1 : 0.1, // Keep in sync with the other Sentry.init()
  profilesSampleRate: __DEV__ ? 1 : 0.1,
});

import { invariant } from "@pinyinly/lib/invariant";
import { Platform } from "react-native";

{
  // Regression test for
  // https://github.com/getsentry/sentry-react-native/issues/2851#issuecomment-1628559234.
  // The problem happened when calling `Sentry.init` as it presumably resolved
  // to an old version of 'promise'.
  invariant((Promise.allSettled as unknown) != null);
}

// Populate the scope with Expo specific information.
{
  const scope = Sentry.getCurrentScope();
  if (Updates.updateId !== null && Updates.updateId !== ``) {
    scope.setTag(`expo-update-id`, Updates.updateId);
  }
  scope.setTag(`expo-is-embedded-update`, Updates.isEmbeddedLaunch);
  scope.setTag(`platform-os`, Platform.OS);
  scope.setTag(`platform-version`, Platform.Version);

  const buildTimestamp = process.env.EXPO_PUBLIC_BUILD_TIMESTAMP;
  if (buildTimestamp !== undefined && buildTimestamp !== ``) {
    scope.setTag(`build-timestamp`, buildTimestamp);
  }

  // Via the guide: https://docs.expo.dev/guides/using-sentry/
  const manifest = Updates.manifest;
  const metadata = `metadata` in manifest ? manifest.metadata : undefined;
  const extra = `extra` in manifest ? manifest.extra : undefined;
  const updateGroup =
    metadata && `updateGroup` in metadata ? metadata.updateGroup : undefined;

  if (typeof updateGroup === `string`) {
    scope.setTag(`expo-update-group-id`, updateGroup);

    const owner = extra?.expoClient?.owner ?? `[account]`;
    const slug = extra?.expoClient?.slug ?? `[project]`;
    scope.setTag(
      `expo-update-debug-url`,
      `https://expo.dev/accounts/${owner}/projects/${slug}/updates/${updateGroup}`,
    );
  } else if (Updates.isEmbeddedLaunch) {
    // This will be `true` if the update is the one embedded in the build, and not one downloaded from the updates server.
    scope.setTag(
      `expo-update-debug-url`,
      `not applicable for embedded updates`,
    );
  }
}
